import { r, s } from '@/theme/tokens';
import {
  Box,
  Skeleton,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Typography,
} from '@/ui/primitives';
import { buildTableDividerPseudoSx } from '../tableSystem/tableDividerSx';
import {
  formatHistoryHeaderDay,
  HEADER_SUBTEXT_SX,
  HEADER_SEPARATOR_SX,
  HEADER_TEXT_SX,
  resolveHistoryDayHeaderFallbackInterval,
  shouldShowDayHeaderLabel,
} from './EntityDailyHistoryPanel.constants';
import type { DailyHistoryCell } from './EntityDailyHistoryPanel.types';
import type { ReactElement } from 'react';

type EntityDailyHistoryPanelSkeletonProps = {
  dayCount: number;
  days?: DailyHistoryCell[];
  entityLabel?: string;
  rowCount: number;
  nameColumnWidth: string;
  rowHeight: number;
  blockSize: number;
  headerHeight: number;
  visibleDayHeaderIndices?: number[];
};

const HISTORY_SKELETON_RADIUS = '999px';
const HISTORY_ROW_NAME_SKELETON_HEIGHT = 16;
const HISTORY_STATUS_BLOCK_SKELETON_HEIGHT = 18;
const ROW_NAME_WIDTHS = ['72%', '64%', '68%', '70%', '62%'] as const;

type VisibleDayHeaderLabel = {
  day: DailyHistoryCell;
  index: number;
};

type HistorySkeletonColumnProps = {
  nameColumnWidth: string;
};

type HistorySkeletonHeaderSectionProps = HistorySkeletonColumnProps & {
  dayCount: number;
  entityLabel: string;
  headerHeight: number;
  visibleDayHeaderLabelsByIndex: Map<number, DailyHistoryCell>;
};

type HistorySkeletonBodySectionProps = HistorySkeletonColumnProps & {
  blockSize: number;
  dayCount: number;
  rowCount: number;
  rowHeight: number;
};

const buildVisibleDayHeaderLabels = (
  days: DailyHistoryCell[],
  visibleDayHeaderIndices?: number[],
): VisibleDayHeaderLabel[] => {
  if (days.length === 0 || !visibleDayHeaderIndices) {
    return [];
  }

  return visibleDayHeaderIndices.flatMap((index) => {
    if (index < 0 || index >= days.length) {
      return [];
    }

    const day = days[index];
    return [{ day, index }];
  });
};

const EntityDailyHistorySkeletonColumns = (): ReactElement => (
  <colgroup>
    <col />
    <col />
  </colgroup>
);

const historySkeletonTableSx = (nameColumnWidth: string): SxProps<Theme> => ({
  tableLayout: 'fixed',
  width: '100%',
  backgroundColor: 'background.paper',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
    borderTop: 'none',
    pt: s('sm'),
  },
  '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
    pb: s('sm'),
  },
  '& .MuiTableCell-root, & .MuiTableRow-root, & .MuiTableHead-root, & .MuiTableBody-root': {
    backgroundColor: 'background.paper',
  },
  '& col:first-of-type': {
    width: nameColumnWidth,
  },
  '& col:last-of-type': {
    width: '100%',
  },
});

const historySkeletonNameCellSx = (nameColumnWidth: string): SxProps<Theme> => [
  {
    backgroundColor: 'background.paper',
    width: nameColumnWidth,
    minWidth: nameColumnWidth,
    maxWidth: nameColumnWidth,
    px: s('md'),
    pt: s('md'),
    pb: s('none'),
    verticalAlign: 'top',
    borderBottom: 'none',
    position: 'relative',
  },
  HEADER_SEPARATOR_SX,
];

const historySkeletonBodyNameCellSx = (nameColumnWidth: string): SxProps<Theme> => [
  {
    backgroundColor: 'background.paper',
    width: nameColumnWidth,
    minWidth: nameColumnWidth,
    maxWidth: nameColumnWidth,
    px: s('md'),
    py: s('none'),
    verticalAlign: 'middle',
    borderBottom: 'none',
  },
];

const EntityDailyHistorySkeletonHeaderSection = ({
  dayCount,
  entityLabel,
  headerHeight,
  nameColumnWidth,
  visibleDayHeaderLabelsByIndex,
}: HistorySkeletonHeaderSectionProps): ReactElement => (
  <TableHead>
    <TableRow
      sx={{
        backgroundColor: 'background.paper',
        height: `${headerHeight}px`,
        position: 'relative',
        '&::after': buildTableDividerPseudoSx('bottom'),
      }}
    >
      <TableCell sx={historySkeletonNameCellSx(nameColumnWidth)}>
        <Typography sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>{entityLabel}</Typography>
      </TableCell>
      <TableCell
        sx={{
          backgroundColor: 'background.paper',
          px: s('md'),
          pt: s('md'),
          pb: s('none'),
          verticalAlign: 'top',
          borderBottom: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('sm'), minWidth: 0 }}>
          <Typography sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>Status</Typography>
          <EntityDailyHistorySkeletonDayHeaderSection
            dayCount={dayCount}
            visibleDayHeaderLabelsByIndex={visibleDayHeaderLabelsByIndex}
          />
        </Box>
      </TableCell>
    </TableRow>
  </TableHead>
);

const EntityDailyHistorySkeletonDayHeaderSection = ({
  dayCount,
  visibleDayHeaderLabelsByIndex,
}: {
  dayCount: number;
  visibleDayHeaderLabelsByIndex: Map<number, DailyHistoryCell>;
}): ReactElement => {
  const headerInterval = resolveHistoryDayHeaderFallbackInterval(dayCount);
  const hasVisibleLabels = visibleDayHeaderLabelsByIndex.size > 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
        columnGap: s('md'),
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        height: '16px',
        pb: s('none'),
      }}
    >
      {Array.from({ length: dayCount }, (_unused, index) => {
        const visibleDay = visibleDayHeaderLabelsByIndex.get(index);
        const showFallbackSpacer =
          !hasVisibleLabels && shouldShowDayHeaderLabel(index, dayCount - 1, headerInterval);

        return (
          <Box
            key={`history-skeleton-header-day-${String(index)}`}
            sx={{
              position: 'relative',
              width: '100%',
              minWidth: 0,
              height: '16px',
              overflow: 'visible',
            }}
          >
            {visibleDay ? (
              <Typography
                sx={[
                  HEADER_SUBTEXT_SX,
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  },
                ]}
              >
                {formatHistoryHeaderDay(visibleDay.day)}
              </Typography>
            ) : showFallbackSpacer ? (
              <Typography
                sx={[
                  HEADER_SUBTEXT_SX,
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  },
                ]}
              >
                &nbsp;
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

const EntityDailyHistorySkeletonBodySection = ({
  blockSize,
  dayCount,
  nameColumnWidth,
  rowCount,
  rowHeight,
}: HistorySkeletonBodySectionProps): ReactElement => (
  <TableBody>
    {Array.from({ length: rowCount }, (_unused, rowIndex) => (
      <EntityDailyHistorySkeletonRow
        key={`history-skeleton-row-${String(rowIndex)}`}
        blockSize={blockSize}
        dayCount={dayCount}
        nameColumnWidth={nameColumnWidth}
        rowHeight={rowHeight}
        rowIndex={rowIndex}
      />
    ))}
  </TableBody>
);

const EntityDailyHistorySkeletonRow = ({
  blockSize,
  dayCount,
  nameColumnWidth,
  rowHeight,
  rowIndex,
}: HistorySkeletonColumnProps & {
  blockSize: number;
  dayCount: number;
  rowHeight: number;
  rowIndex: number;
}): ReactElement => (
  <TableRow
    sx={{
      backgroundColor: 'background.paper',
      height: `${rowHeight}px`,
    }}
  >
    <TableCell sx={historySkeletonBodyNameCellSx(nameColumnWidth)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('sm'),
          minHeight: `${rowHeight}px`,
          minWidth: 0,
        }}
      >
        <Skeleton
          variant="rounded"
          width={ROW_NAME_WIDTHS[rowIndex % ROW_NAME_WIDTHS.length]}
          sx={{
            height: `${HISTORY_ROW_NAME_SKELETON_HEIGHT}px`,
            transform: 'none',
            borderRadius: HISTORY_SKELETON_RADIUS,
            flexShrink: 0,
          }}
        />
      </Box>
    </TableCell>
    <TableCell
      sx={[
        {
          backgroundColor: 'background.paper',
          px: s('md'),
          py: s('none'),
          verticalAlign: 'middle',
          borderBottom: 'none',
          width: '100%',
        },
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: `${rowHeight}px` }}>
        <EntityDailyHistorySkeletonStatusCells dayCount={dayCount} blockSize={blockSize} />
      </Box>
    </TableCell>
  </TableRow>
);

const EntityDailyHistorySkeletonStatusCells = ({
  blockSize,
  dayCount,
}: {
  blockSize: number;
  dayCount: number;
}): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
      columnGap: s('md'),
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
    }}
  >
    {Array.from({ length: dayCount }, (_dayValue, dayIndex) => (
      <Skeleton
        key={`history-skeleton-cell-${String(dayIndex)}`}
        variant="rounded"
        sx={{
          width: '100%',
          minWidth: `${blockSize}px`,
          height: `${HISTORY_STATUS_BLOCK_SKELETON_HEIGHT}px`,
          transform: 'none',
          borderRadius: r('sm'),
        }}
      />
    ))}
  </Box>
);

export const EntityDailyHistoryPanelSkeleton = ({
  dayCount,
  days = [],
  entityLabel = 'Entity',
  rowCount,
  nameColumnWidth,
  rowHeight,
  blockSize,
  headerHeight,
  visibleDayHeaderIndices,
}: EntityDailyHistoryPanelSkeletonProps): ReactElement => {
  const visibleDayHeaderLabels = buildVisibleDayHeaderLabels(days, visibleDayHeaderIndices);
  const visibleDayHeaderLabelsByIndex = new Map(
    visibleDayHeaderLabels.map((entry) => [entry.index, entry.day] as const),
  );

  return (
    <Box>
      <Table aria-label="Loading history" sx={historySkeletonTableSx(nameColumnWidth)}>
        <EntityDailyHistorySkeletonColumns />
        <EntityDailyHistorySkeletonHeaderSection
          dayCount={dayCount}
          entityLabel={entityLabel}
          headerHeight={headerHeight}
          nameColumnWidth={nameColumnWidth}
          visibleDayHeaderLabelsByIndex={visibleDayHeaderLabelsByIndex}
        />
        <EntityDailyHistorySkeletonBodySection
          blockSize={blockSize}
          dayCount={dayCount}
          nameColumnWidth={nameColumnWidth}
          rowCount={rowCount}
          rowHeight={rowHeight}
        />
      </Table>
    </Box>
  );
};
